/**
 * SpaceFeed.jsx — v2.1
 * Place at: src/pages/SpaceFeed.jsx
 *
 * CHANGES from v2:
 *   - On mobile (<= 680px) the right sidebar (About / Rules / Stats) is hidden
 *   - A ☰ button appears in the header (top-right corner) on mobile
 *   - Tapping it slides up a bottom sheet with About, Rules, and Stats
 *   - Desktop layout is COMPLETELY UNCHANGED
 *   - No other changes
 */

import { useState, useCallback } from "react";
import { useSpaceFeed }     from "../hooks/useSpaceFeed";
import { useSpaceMetadata } from "../hooks/useSpaceMetadata";
import PostCard             from "../components/PostCard";
import PostComposer         from "../components/PostComposer";
import ModeratorPanel       from "../components/ModeratorPanel";
import {
  joinSpace, leaveSpace,
  pinSpacePost, unpinSpacePost, removePostFromSpace,
} from "../lib/spaceQueries";
import { formatTimeAgo } from "../lib/utils";

const SORT_OPTIONS = [
  { value: "new", label: "🆕 New"  },
  { value: "hot", label: "🔥 Hot"  },
  { value: "top", label: "⭐ Top"  },
];

/* ─── detect mobile once ─────────────────────────────────────────────────── */
const isMobileViewport = () =>
  typeof window !== "undefined" && window.innerWidth <= 680;

export default function SpaceFeed({ spaceId, currentUser, onClose }) {
  const userId = currentUser?.id;

  const {
    metadata, loading: metaLoading, error: metaError, invalidate: invalidateMeta,
  } = useSpaceMetadata(spaceId, userId);

  const {
    posts, loading, loadingMore, hasMore, error: feedError,
    sort, staleBanner,
    loadMore, changeSort, addPost, removePost,
    dismissStaleBanner, invalidateCache,
  } = useSpaceFeed(spaceId, userId);

  const [joining,      setJoining]      = useState(false);
  const [showModPanel, setShowModPanel] = useState(false);
  const [joinError,    setJoinError]    = useState(null);

  /* ── NEW: mobile info sheet ─────────────────────────────────────────── */
  const [showInfoSheet, setShowInfoSheet] = useState(false);

  const isMember  = metadata?.is_member;
  const isMod     = metadata?.is_moderator;
  const isMuted   = metadata?.is_muted;
  const isBanned  = metadata?.is_banned;
  const canPost   = isMember && !isMuted && !isBanned;
  const accent    = metadata?.color || "#6366f1";

  // ── Join ────────────────────────────────────────────────────────────────────
  const handleJoin = useCallback(async () => {
    if (!userId) return;
    setJoining(true);
    setJoinError(null);
    try {
      await joinSpace(spaceId, userId);
      invalidateMeta();
    } catch (err) {
      setJoinError(
        err.message?.includes("banned")
          ? "You've been removed from this space."
          : "Failed to join. Please try again."
      );
    } finally {
      setJoining(false);
    }
  }, [spaceId, userId, invalidateMeta]);

  // ── Leave ───────────────────────────────────────────────────────────────────
  const handleLeave = useCallback(async () => {
    if (!window.confirm("Leave this space? You can rejoin anytime.")) return;
    try {
      await leaveSpace(spaceId, userId);
      invalidateMeta();
    } catch (err) {
      alert(err.message || "Failed to leave space.");
    }
  }, [spaceId, userId, invalidateMeta]);

  const handlePostCreated = useCallback((post) => {
    addPost({ ...post, space_id: spaceId });
  }, [addPost, spaceId]);

  const handlePinPost = useCallback(async (postId) => {
    try { await pinSpacePost(spaceId, postId, userId); invalidateCache(); }
    catch (err) { alert(err.message || "Failed to pin post."); }
  }, [spaceId, userId, invalidateCache]);

  const handleUnpinPost = useCallback(async (postId) => {
    try { await unpinSpacePost(spaceId, postId, userId); invalidateCache(); }
    catch (err) { alert(err.message || "Failed to unpin post."); }
  }, [spaceId, userId, invalidateCache]);

  const handleRemoveFromSpace = useCallback(async (postId) => {
    try { await removePostFromSpace(spaceId, postId, userId); removePost(postId); }
    catch (err) { alert(err.message || "Failed to remove post."); }
  }, [spaceId, userId, removePost]);

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (metaLoading) return (
    <div style={overlayStyle}>
      <div style={containerStyle}>
        <div style={{ display:"flex", justifyContent:"center", alignItems:"center", height:200, color:"var(--text-muted)", flexDirection:"column", gap:12 }}>
          <div style={{ width:32, height:32, borderRadius:"50%", border:`3px solid ${accent}`, borderTopColor:"transparent", animation:"spin 0.8s linear infinite" }} />
          Loading space…
        </div>
      </div>
    </div>
  );

  if (metaError || !metadata) return (
    <div style={overlayStyle} onClick={e => { if (e.target===e.currentTarget) onClose?.(); }}>
      <div style={containerStyle}>
        <div style={{ padding:32, textAlign:"center", color:"var(--text-muted)" }}>
          Space not found.
          <button onClick={onClose} style={{ marginLeft:8, color:"var(--primary-color)", background:"none", border:"none", cursor:"pointer" }}>← Go back</button>
        </div>
      </div>
    </div>
  );

  /* ── Sidebar content (shared between desktop sidebar & mobile sheet) ── */
  const SidebarContent = () => (
    <>
      {/* About */}
      <div style={{ ...sideCard, borderTop:`3px solid ${accent}` }}>
        <div style={sideTitle}>About</div>
        <p style={{ fontSize:"0.82rem", color:"var(--text-muted)", lineHeight:1.6, margin:"0 0 10px" }}>
          {metadata.description}
        </p>
        {metadata.tag && (
          <span style={{
            display:"inline-block", padding:"3px 10px", borderRadius:20,
            background:`${accent}15`, color:accent, fontSize:"0.72rem", fontWeight:700,
          }}>
            #{metadata.tag}
          </span>
        )}
      </div>

      {/* Rules */}
      {metadata.rules?.filter(r => r?.trim()).length > 0 && (
        <div style={sideCard}>
          <div style={sideTitle}>📋 Rules</div>
          <ol style={{ margin:0, paddingLeft:18, display:"flex", flexDirection:"column", gap:6 }}>
            {metadata.rules.filter(r => r?.trim()).map((rule, i) => (
              <li key={i} style={{ fontSize:"0.8rem", color:"var(--text-color)", lineHeight:1.5 }}>
                {rule}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Stats */}
      <div style={sideCard}>
        <div style={sideTitle}>📊 Stats</div>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {[
            ["Members",       metadata.member_count?.toLocaleString()],
            ["Posts",         metadata.post_count?.toLocaleString()],
            ["New this week", `+${metadata.new_members_this_week || 0}`],
            ["Created",       formatTimeAgo(metadata.created_at)],
          ].map(([label, value]) => (
            <div key={label} style={{ display:"flex", justifyContent:"space-between", fontSize:"0.8rem" }}>
              <span style={{ color:"var(--text-muted)" }}>{label}</span>
              <span style={{ fontWeight:700, color:"var(--text-color)" }}>{value}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  return (
    <div style={overlayStyle} onClick={e => { if (e.target===e.currentTarget) onClose?.(); }}>
      <div style={containerStyle}>

        {/* ── HEADER ─────────────────────────────────────────────── */}
        <div style={{
          background:     `linear-gradient(135deg, ${accent}15 0%, var(--card-bg-color) 100%)`,
          borderBottom:   `1px solid ${accent}25`,
          padding:        "22px 24px 18px",
          position:       "sticky",
          top:            0,
          zIndex:         10,
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        }}>
          <div style={{ display:"flex", alignItems:"flex-start", gap:14 }}>
            {/* Emoji badge */}
            <div style={{
              width:56, height:56, borderRadius:16, flexShrink:0,
              background:     `${accent}20`,
              border:         `2px solid ${accent}35`,
              display:        "flex",
              alignItems:     "center",
              justifyContent: "center",
              fontSize:       28,
              boxShadow:      `0 4px 14px ${accent}20`,
            }}>
              {metadata.avatar_emoji || "🏡"}
            </div>

            <div style={{ flex:1, minWidth:0 }}>
              {/* Name + badges */}
              <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:4 }}>
                <h2 style={{ margin:0, fontSize:"1.2rem", fontWeight:800, color:"var(--text-color)", lineHeight:1.2 }}>
                  {metadata.name}
                </h2>
                {metadata.moderated_mode && (
                  <span style={{ fontSize:"0.65rem", fontWeight:700, color:"#f97316", background:"#f9731618", padding:"2px 7px", borderRadius:20, border:"1px solid #f9731630" }}>
                    🔒 Moderated
                  </span>
                )}
                {metadata.health_label === "thriving" && (
                  <span style={{ fontSize:"0.65rem", fontWeight:700, color:"#22c55e", background:"#22c55e18", padding:"2px 7px", borderRadius:20 }}>
                    🟢 Thriving
                  </span>
                )}
              </div>

              {/* Stats */}
              <div style={{ fontSize:"0.78rem", color:"var(--text-muted)", marginBottom:10, display:"flex", gap:12, flexWrap:"wrap" }}>
                <span>🛡️ @{metadata.moderator_username}</span>
                <span>👥 {metadata.member_count?.toLocaleString()} members</span>
                <span>📝 {metadata.post_count?.toLocaleString()} posts</span>
              </div>

              {/* Actions */}
              <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
                {!isMember ? (
                  <button
                    onClick={handleJoin}
                    disabled={joining}
                    style={{
                      padding:      "8px 20px",
                      borderRadius: 10,
                      border:       "none",
                      background:   joining ? `${accent}80` : accent,
                      color:        "#fff",
                      fontWeight:   700,
                      fontSize:     "0.85rem",
                      cursor:       joining ? "not-allowed" : "pointer",
                      fontFamily:   "inherit",
                      transition:   "all 0.2s",
                      boxShadow:    joining ? "none" : `0 4px 12px ${accent}40`,
                      transform:    joining ? "none" : "translateY(0)",
                    }}
                    onMouseEnter={e => { if (!joining) { e.currentTarget.style.transform="translateY(-1px)"; e.currentTarget.style.boxShadow=`0 6px 16px ${accent}50`; }}}
                    onMouseLeave={e => { e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow=`0 4px 12px ${accent}40`; }}
                  >
                    {joining ? "Joining…" : "✨ Join Space"}
                  </button>
                ) : (
                  <>
                    {isMod && (
                      <button
                        onClick={() => setShowModPanel(true)}
                        style={{
                          padding:"8px 16px", borderRadius:10,
                          border:`1.5px solid ${accent}50`,
                          background:`${accent}12`,
                          color:accent, fontWeight:700,
                          fontSize:"0.82rem", cursor:"pointer",
                          fontFamily:"inherit", transition:"all 0.15s",
                        }}
                        onMouseEnter={e => e.currentTarget.style.background=`${accent}20`}
                        onMouseLeave={e => e.currentTarget.style.background=`${accent}12`}
                      >
                        🛡️ Mod Panel
                      </button>
                    )}
                    <button
                      onClick={handleLeave}
                      style={{
                        padding:"8px 14px", borderRadius:10,
                        border:"1.5px solid var(--border-color)",
                        background:"transparent",
                        color:"var(--text-muted)", fontWeight:600,
                        fontSize:"0.82rem", cursor:"pointer",
                        fontFamily:"inherit", transition:"all 0.15s",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background="var(--bg-secondary)"; e.currentTarget.style.color="var(--text-color)"; }}
                      onMouseLeave={e => { e.currentTarget.style.background="transparent"; e.currentTarget.style.color="var(--text-muted)"; }}
                    >
                      Leave
                    </button>
                  </>
                )}

                {onClose && (
                  <button
                    onClick={onClose}
                    style={{
                      padding:"8px 14px", borderRadius:10,
                      border:"1.5px solid var(--border-color)",
                      background:"transparent", color:"var(--text-muted)",
                      fontSize:"0.82rem", cursor:"pointer", fontFamily:"inherit",
                    }}
                  >
                    ✕ Close
                  </button>
                )}

                {/* ── ☰ Info button — mobile only ── */}
                <button
                  onClick={() => setShowInfoSheet(true)}
                  className="sf-info-btn"
                  title="About this space"
                  style={{
                    padding:"8px 12px", borderRadius:10,
                    border:`1.5px solid ${accent}40`,
                    background:`${accent}0d`,
                    color:accent, fontWeight:700,
                    fontSize:"1rem", cursor:"pointer",
                    fontFamily:"inherit", lineHeight:1,
                    display:"none", /* shown via CSS below */
                  }}
                >☰</button>
              </div>

              {joinError && (
                <div style={{ marginTop:8, fontSize:"0.78rem", color:"#ef4444", background:"#ef444410", padding:"5px 10px", borderRadius:8, border:"1px solid #ef444430" }}>
                  {joinError}
                </div>
              )}
            </div>
          </div>

          {/* Weekly prompt banner */}
          {metadata.weekly_prompt && (
            <div style={{
              marginTop:    16,
              padding:      "11px 16px",
              background:   `${accent}10`,
              border:       `1px solid ${accent}25`,
              borderRadius: 12,
            }}>
              <div style={{ fontSize:"0.68rem", fontWeight:800, color:accent, marginBottom:4, textTransform:"uppercase", letterSpacing:"0.06em" }}>
                💬 This Week's Question
              </div>
              <div style={{ fontSize:"0.9rem", color:"var(--text-color)", fontStyle:"italic", lineHeight:1.5 }}>
                "{metadata.weekly_prompt}"
              </div>
            </div>
          )}
        </div>

        {/* ── MAIN CONTENT ───────────────────────────────────────── */}
        <div style={{
          display:              "grid",
          gridTemplateColumns:  "1fr 250px",
          gap:                  20,
          padding:              "20px 24px 24px",
          alignItems:           "start",
        }}
          className="sf-layout"
        >

          {/* LEFT — Feed */}
          <div>
            {/* Notices */}
            {isMuted && <Notice color="#f97316" msg="🔇 You are muted and cannot post in this space." />}
            {isBanned && <Notice color="#ef4444" msg="🚫 You have been removed from this space." />}
            {!isMember && !isBanned && (
              <Notice color={accent} msg="👀 You're viewing as a guest. Join to post and interact." />
            )}

            {/* Stale banner */}
            {staleBanner && (
              <div style={{
                padding:"9px 14px", borderRadius:10, marginBottom:14,
                background:`${accent}10`, border:`1px solid ${accent}25`,
                display:"flex", justifyContent:"space-between", alignItems:"center",
              }}>
                <span style={{ fontSize:"0.82rem", color:"var(--text-muted)" }}>New posts may be available</span>
                <button onClick={dismissStaleBanner} style={{
                  padding:"4px 12px", borderRadius:8, border:"none",
                  background:accent, color:"#fff",
                  fontSize:"0.75rem", fontWeight:600, cursor:"pointer", fontFamily:"inherit",
                }}>Refresh</button>
              </div>
            )}

            {/* Post composer — only for members */}
            {canPost && (
              <PostComposer
                feedUser={currentUser}
                spaceId={spaceId}
                spaceName={metadata.name}
                onPostCreated={handlePostCreated}
              />
            )}

            {/* Sort tabs */}
            <div style={{ display:"flex", gap:6, marginBottom:16 }}>
              {SORT_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => changeSort(opt.value)}
                  style={{
                    padding:"6px 14px", borderRadius:20, fontFamily:"inherit",
                    border:     sort === opt.value ? `1.5px solid ${accent}` : "1.5px solid var(--border-color)",
                    background: sort === opt.value ? `${accent}15` : "transparent",
                    color:      sort === opt.value ? accent : "var(--text-muted)",
                    fontSize:"0.78rem", fontWeight:600, cursor:"pointer", transition:"all 0.15s",
                  }}
                >{opt.label}</button>
              ))}
            </div>

            {feedError && <Notice color="#ef4444" msg={feedError} />}

            {/* Posts */}
            {loading && posts.length === 0 ? (
              <div style={{ textAlign:"center", padding:48, color:"var(--text-muted)" }}>
                <div style={{ width:28, height:28, borderRadius:"50%", border:`3px solid ${accent}`, borderTopColor:"transparent", animation:"spin 0.8s linear infinite", margin:"0 auto 12px" }} />
                Loading posts…
              </div>
            ) : posts.length === 0 ? (
              <div style={{
                textAlign:"center", padding:"40px 24px",
                borderRadius:14, border:"1.5px dashed var(--border-color)", background:"var(--card-bg-color)",
                color:"var(--text-muted)",
              }}>
                <div style={{ fontSize:"2.5rem", marginBottom:12 }}>✨</div>
                <div style={{ fontWeight:700, marginBottom:6, color:"var(--text-color)" }}>No posts yet</div>
                {canPost
                  ? "Be the first to post in this space!"
                  : isMember
                  ? "Nothing here yet — check back soon."
                  : "Join this space to see and post content."}
              </div>
            ) : (
              <>
                {posts.map(post => (
                  <div key={post.id}>
                    <PostCard
                      post={post}
                      currentUser={currentUser}
                      onDelete={id => removePost(id)}
                      extraActions={isMod ? [
                        post.is_pinned
                          ? { label:"📌 Unpin", onClick: () => handleUnpinPost(post.id) }
                          : { label:"📌 Pin",   onClick: () => handlePinPost(post.id)   },
                        { label:"🗑️ Remove from space", onClick: () => handleRemoveFromSpace(post.id) },
                      ] : []}
                    />
                  </div>
                ))}

                {hasMore && (
                  <div style={{ textAlign:"center", marginTop:16 }}>
                    <button
                      onClick={loadMore}
                      disabled={loadingMore}
                      style={{
                        padding:"9px 28px", borderRadius:20,
                        border:"1.5px solid var(--border-color)",
                        background:"transparent", color:"var(--text-muted)",
                        fontSize:"0.85rem", cursor: loadingMore ? "not-allowed" : "pointer",
                        fontFamily:"inherit", opacity: loadingMore ? 0.6 : 1,
                      }}
                    >
                      {loadingMore ? "Loading…" : "Load more"}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* RIGHT — Sidebar (desktop only, hidden on mobile via CSS) */}
          <div style={{ position:"sticky", top:210, display:"flex", flexDirection:"column", gap:12 }}
            className="sf-sidebar"
          >
            <SidebarContent />
          </div>
        </div>
      </div>

      {/* ── MOBILE INFO BOTTOM SHEET ──────────────────────────────────────
          Rendered at root level so it overlays everything.
          Slides up from bottom. Tap backdrop to dismiss.
      ─────────────────────────────────────────────────────────────────── */}
      {showInfoSheet && (
        <div
          style={{
            position:"fixed", inset:0, zIndex:1000,
            background:"rgba(0,0,0,.52)",
            backdropFilter:"blur(6px)", WebkitBackdropFilter:"blur(6px)",
            display:"flex", flexDirection:"column", justifyContent:"flex-end",
            animation:"sfSheetBg .18s ease",
          }}
          onClick={() => setShowInfoSheet(false)}
        >
          <div
            style={{
              background:"var(--bg-secondary, #F9F4F0)",
              borderRadius:"24px 24px 0 0",
              maxHeight:"82dvh",
              display:"flex", flexDirection:"column",
              boxShadow:"0 -8px 32px rgba(0,0,0,.14)",
              animation:"sfSheetUp .3s cubic-bezier(.25,1,.4,1)",
              overflow:"hidden",
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Handle */}
            <div style={{
              width:36, height:4, borderRadius:99,
              background:"var(--border-color, #D6D2C4)",
              margin:"12px auto 0", flexShrink:0,
            }} />

            {/* Sheet header */}
            <div style={{
              display:"flex", alignItems:"center", justifyContent:"space-between",
              padding:"13px 18px",
              borderBottom:`0.5px solid ${accent}20`,
              flexShrink:0,
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{
                  width:34, height:34, borderRadius:10, flexShrink:0,
                  background:`${accent}18`, border:`1.5px solid ${accent}28`,
                  display:"flex", alignItems:"center", justifyContent:"center", fontSize:18,
                }}>{metadata.avatar_emoji || "🏡"}</div>
                <span style={{
                  fontSize:"1rem", fontWeight:800,
                  color:"var(--text-color, #2D364D)",
                }}>{metadata.name}</span>
              </div>
              <button
                onClick={() => setShowInfoSheet(false)}
                style={{
                  width:30, height:30, borderRadius:"50%",
                  background:"rgba(118,118,128,.13)", border:"none",
                  color:"var(--text-color, #2D364D)", fontWeight:700, fontSize:".85rem",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  cursor:"pointer",
                }}
              >✕</button>
            </div>

            {/* Scrollable content */}
            <div style={{
              flex:1, overflowY:"auto",
              padding:"14px 16px calc(env(safe-area-inset-bottom,0px) + 20px)",
              display:"flex", flexDirection:"column", gap:12,
              /* hide scrollbar */
              scrollbarWidth:"none",
              msOverflowStyle:"none",
            }}>
              <SidebarContent />
            </div>
          </div>
        </div>
      )}

      {/* Mod Panel */}
      {showModPanel && (
        <ModeratorPanel
          space={{ ...metadata, id: spaceId }}
          currentUser={currentUser}
          onClose={() => setShowModPanel(false)}
          onSpaceUpdated={() => invalidateMeta()}
        />
      )}

      {/* ── Keyframes + responsive CSS ───────────────────────────────────── */}
      <style>{`
        @keyframes spin        { to { transform: rotate(360deg); } }
        @keyframes sfSheetBg   { from { opacity:0 } to { opacity:1 } }
        @keyframes sfSheetUp   { from { transform:translateY(100%) } to { transform:translateY(0) } }

        /* Show ☰ button only on mobile */
        .sf-info-btn { display: none !important; }

        @media (max-width: 680px) {
          /* Show the ☰ info button */
          .sf-info-btn { display: flex !important; align-items: center; justify-content: center; }

          /* Collapse to single-column, remove desktop sidebar */
          .sf-layout {
            grid-template-columns: 1fr !important;
            gap: 0 !important;
            padding: 14px 12px 24px !important;
          }
          .sf-sidebar { display: none !important; }

          /* Tighten mobile scrollbar hide for sheet */
          .sf-sheet-scroll::-webkit-scrollbar { display: none; }
        }
      `}</style>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const Notice = ({ color, msg }) => (
  <div style={{
    padding:"9px 14px", borderRadius:10, marginBottom:14,
    background:`${color}10`, border:`1px solid ${color}30`,
    color, fontSize:"0.82rem", fontWeight:600,
  }}>{msg}</div>
);

const overlayStyle = {
  position:"fixed", inset:0,
  background:"rgba(0,0,0,0.55)",
  zIndex:900, overflowY:"auto",
  display:"flex", justifyContent:"center",
};

const containerStyle = {
  background:"var(--bg-secondary)",
  width:"100%", maxWidth:980,
  minHeight:"100vh",
  boxShadow:"0 0 60px rgba(0,0,0,0.2)",
};

const sideCard = {
  background:"var(--card-bg-color)",
  borderRadius:12,
  border:"1px solid var(--border-color)",
  padding:"14px 16px",
};

const sideTitle = {
  fontSize:"0.72rem", fontWeight:800,
  color:"var(--text-muted)",
  textTransform:"uppercase",
  letterSpacing:"0.06em",
  marginBottom:10,
};